export const validateEmail = (input: string): boolean => {
  // Check if input is empty or just whitespace
  if (!input) {
    return true;
  }

  // Split by semicolon or colon
  const emails = input
    .split(/[;:]/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  // Must have at least one email
  if (emails.length === 0) {
    return false;
  }

  // Email regex pattern that properly handles dots
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Check if all emails are valid
  return emails.every((email) => {
    // First check with regex
    if (!emailRegex.test(email)) {
      return false;
    }

    // Additional validation for dots
    const [localPart, domainPart] = email.split('@');

    // Check local part (before @)
    if (
      localPart.startsWith('.') ||
      localPart.endsWith('.') ||
      localPart.includes('..')
    ) {
      return false;
    }

    // Check domain part (after @)
    if (domainPart.startsWith('.') || domainPart.includes('..')) {
      return false;
    }

    return true;
  });
};

export const validatePhone = (value: string) => {
  if (!value) return true;
  if (value.length > 20) return false;

  // More strict regex that doesn't allow consecutive special characters
  // and properly validates parentheses
  const phoneRegex =
    /^(\+?[0-9]{1,4})?(\s?\(?[0-9]{1,4}\)?)?(([\s\-./])?[0-9]){1,14}$/;

  // Additional validation for parentheses
  const openParens = (value.match(/\(/g) || []).length;
  const closeParens = (value.match(/\)/g) || []).length;

  // Check for consecutive special characters
  const hasConsecutiveSpecial = /[\s\-./]{2,}/.test(value);

  return (
    phoneRegex.test(value) &&
    openParens === closeParens &&
    openParens <= 1 &&
    !hasConsecutiveSpecial
  );
};

export const validateCharacterCount = (
  value: string | number,
  characterLimit: number
) => {
  if (value && value.toString().length > characterLimit) return false;
  return true;
};

//AUS

export const validateABN = (rawValue: string) => {
  const value = rawValue.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  // Must be exactly 11 digits
  if (!value || !/^\d{11}$/.test(value)) return false;

  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

  // Subtract 1 from first digit as per ABN algorithm
  let weighted = (parseInt(value[0]) - 1) * weights[0];

  // Apply weights to remaining digits
  for (let i = 1; i < 11; i++) {
    weighted += parseInt(value[i]) * weights[i];
  }

  return weighted % 89 === 0;
};

export const validateABNDivisionNumber = (rawValue: string) => {
  const value = rawValue.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  if (!value || !/^\d{3}$/.test(value)) return false;

  const num = parseInt(value);
  return num >= 0 && num <= 999;
};

export const validateACN = (rawValue: string) => {
  const value = rawValue.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  if (!value || !/^\d{9}$/.test(value)) return false;

  const weights = [8, 7, 6, 5, 4, 3, 2, 1];

  const sum = weights.reduce((total, weight, i) => {
    return total + weight * parseInt(value[i]);
  }, 0);

  const checkDigit = (10 - (sum % 10)) % 10;

  return parseInt(value[8]) === checkDigit;
};

export const validateTFN = (rawValue: string) => {
  const value = rawValue.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  if (!value || !/^\d{9}$/.test(value)) return false;

  const digits = value.split('').map((n) => parseInt(n));
  let weightFactor: number;

  if (value.length === 9) {
    // Standard 9 digit TFN weights
    const weights = [1, 4, 3, 7, 5, 8, 6, 9, 10];
    weightFactor = digits.reduce(
      (sum, digit, i) => sum + digit * weights[i],
      0
    );
  } else {
    // Old 8 digit TFN weights
    const weights = [10, 7, 8, 4, 6, 3, 5, 1];
    weightFactor = digits.reduce(
      (sum, digit, i) => sum + digit * weights[i],
      0
    );
  }

  return weightFactor % 11 === 0;
};

//NZ

export function validateNZBN(rawValue: string): boolean {
  const value = rawValue.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  if (!value) return false;

  // Check if it contains only digits
  if (!/^\d+$/.test(value)) {
    return false;
  }

  // NZBN must be exactly 13 digits
  if (value.length !== 13) {
    return false;
  }

  // Extract the check digit (last digit)
  const checkDigit = parseInt(value[12], 10);

  // Calculate checksum using GS1 algorithm
  let oddSum = 0;
  let evenSum = 0;

  // Process the first 12 digits (excluding check digit)
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(value[i], 10);

    // Position numbering starts from 1, so i+1 is the position
    if ((i + 1) % 2 === 1) {
      // Odd positions (1st, 3rd, 5th, etc.)
      oddSum += digit;
    } else {
      // Even positions (2nd, 4th, 6th, etc.)
      evenSum += digit;
    }
  }

  // Apply GS1 algorithm
  const total = oddSum * 3 + evenSum;
  const remainder = total % 10;
  const calculatedCheckDigit = remainder === 0 ? 0 : 10 - remainder;

  return calculatedCheckDigit === checkDigit;
}

const calculateCheckDigit = (
  irdDigits: number[],
  weights: number[]
): number => {
  const sum = irdDigits.reduce(
    (total, digit, index) => total + digit * weights[index],
    0
  );
  const remainder = sum % 11;
  return remainder === 0 ? 0 : 11 - remainder;
};

const padWithZeros = (str: string, length: number): string => {
  return str.padStart(length, '0');
};

export function validateIRD(rawValue: string): boolean {
  const value = rawValue.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  // Handle null, undefined, or non-string inputs
  if (!value || typeof value !== 'string') {
    return false;
  }

  // Check for invalid characters (only digits, spaces, and dashes allowed)
  if (!/^[\d\s-]*$/.test(value)) {
    return false;
  }

  // If the original input contains dashes or spaces, it should be rejected
  // (based on your test expectation that formatted numbers are invalid)
  if (/[- ]/.test(value)) {
    return false;
  }

  // Must be 8 or 9 digits only
  if (!/^\d{8,9}$/.test(value)) {
    return false;
  }

  // Convert to number for range check
  const irdAsNumber = parseInt(value, 10);
  if (irdAsNumber < 10000000 || irdAsNumber > 150000000) {
    return false;
  }

  // Extract check digit and base IRD digits
  const checkDigit = parseInt(value.slice(-1), 10);
  const baseIrdString = padWithZeros(value.slice(0, -1), 8);
  const baseIrdDigits = baseIrdString
    .split('')
    .map((digit) => parseInt(digit, 10));

  // Primary weight factors
  const primaryWeights = [3, 2, 7, 6, 5, 4, 3, 2];
  const primaryCheckDigit = calculateCheckDigit(baseIrdDigits, primaryWeights);

  if (primaryCheckDigit < 10) {
    return primaryCheckDigit === checkDigit;
  }

  // Secondary weight factors (used when primary calculation results in 10)
  const secondaryWeights = [7, 4, 3, 2, 5, 2, 7, 6];
  const secondaryCheckDigit = calculateCheckDigit(
    baseIrdDigits,
    secondaryWeights
  );

  return secondaryCheckDigit < 10 && secondaryCheckDigit === checkDigit;
}
